import { EventsListFilters, EventsListParams } from '../types/events.types';
import { EventMenuType } from '../types/events.types';
import { EventType } from '../../../types/events/common.types';

import { FiltersState } from '../constants';
import {
  eventTypeMap,
  orderingMap,
  rewardTypeMap,
  sortByMap,
  statusMap,
  validateEventStatusNumbers,
  validateRewardTypeStrings,
  validateSearch,
} from './validation';

const mapMultiSelectFilter = <T>(
  filter: Record<string, boolean>,
  map: Record<string, T>,
  excludeKey: string = 'ALL'
): T[] => {
  const selected: T[] = [];
  Object.entries(filter).forEach(([key, selectedValue]) => {
    if (selectedValue && key !== excludeKey && key in map) {
      selected.push(map[key as keyof typeof map]);
    }
  });
  return selected;
};

export const mapFiltersStateToApiFilters = (filtersState: FiltersState): EventsListFilters => {
  const apiFilters: EventsListFilters = {};

  if (filtersState.sortBy && sortByMap[filtersState.sortBy as keyof typeof sortByMap]) {
    apiFilters.sortBy = sortByMap[filtersState.sortBy as keyof typeof sortByMap];
  }

  const selectedStatuses = mapMultiSelectFilter(filtersState.status, statusMap);
  if (selectedStatuses.length > 0) {
    apiFilters.status = selectedStatuses;
  }

  if (filtersState.special.VIP_ONLY) {
    apiFilters.vipOnly = true;
  }

  const selectedRewardTypes = mapMultiSelectFilter(filtersState.rewardType, rewardTypeMap);
  if (selectedRewardTypes.length > 0) {
    apiFilters.rewardType = selectedRewardTypes;
  }

  if (filtersState.eventType && filtersState.eventType !== EventMenuType.ALL && filtersState.eventType !== EventMenuType.FINISHED) {
    const mappedEventType = eventTypeMap[filtersState.eventType as EventMenuType];
    if (mappedEventType && mappedEventType !== 'all' && mappedEventType !== 'finished') {
      apiFilters.eventType = [mappedEventType as EventType];
    }
  }

  const validatedSearch = validateSearch(filtersState.search);
  if (validatedSearch) {
    apiFilters.search = validatedSearch;
  }

  return apiFilters;
};

export const mapFiltersToParams = (filters?: EventsListFilters): Partial<EventsListParams> => {
  if (!filters) return {};

  const params: Partial<EventsListParams> = {};

  if (filters.sortBy && filters.sortBy in orderingMap) {
    params.ordering = orderingMap[filters.sortBy];
  }

  if (filters.status && filters.status.length > 0) {
    const validStatuses = validateEventStatusNumbers(filters.status);
    if (validStatuses.length > 0) {
      params.status = validStatuses.length === 1 ? validStatuses[0] : validStatuses;
    }
  }

  if (filters.vipOnly) {
    params.event_label = 'vip';
  }

  if (filters.eventType && filters.eventType.length > 0) {
    params.event_type = filters.eventType;
  }

  const validatedSearch = validateSearch(filters.search);
  if (validatedSearch) {
    params.search = validatedSearch;
  }

  if (filters.rewardType && filters.rewardType.length > 0) {
    const validRewardTypes = validateRewardTypeStrings(filters.rewardType);
    if (validRewardTypes.length > 0) {
      params.reward_type = validRewardTypes.length === 1 ? validRewardTypes[0] : validRewardTypes;
    }
  }

  return params;
};
